import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, FlaskConical, Clock, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";

/**
 * Category & Tag listing page. `mode` = 'category' | 'tag'.
 */
export default function ResearchListing({ mode }) {
  const { slug } = useParams();
  const [items, setItems] = useState(null);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    setItems(null);
    const p = new URLSearchParams();
    p.set(mode === "category" ? "category" : "tag", slug);
    api.get(`/research?${p}`).then((r) => setItems(r.data)).catch(() => setItems([]));
    api.get(mode === "category" ? "/research-categories/list" : "/research-tags/list").then((r) => {
      setMeta((r.data || []).find((x) => x.slug === slug));
    });
  }, [slug, mode]);

  const title = meta?.name || slug;

  return (
    <div data-testid={`research-${mode}-page`} className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
      <Link to="/research" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4"><ArrowLeft className="w-4 h-4"/> Research Hub</Link>
      <div className="mb-8">
        <div className="eyebrow mb-2">{mode === "category" ? "Category" : "Tag"}</div>
        <h1 className="font-display text-4xl font-semibold text-slate-900 tracking-tight">{title}</h1>
        {meta?.description && <p className="mt-2 text-slate-600 max-w-2xl">{meta.description}</p>}
      </div>

      {items === null ? <div className="text-slate-400 text-sm font-mono uppercase tracking-widest">Loading…</div> :
        items.length === 0 ? <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded p-10 text-center">No articles in this {mode} yet.</div> : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((a) => (
              <Link key={a.slug} to={`/research/${a.slug}`} className="block border border-slate-200 rounded-md overflow-hidden bg-white hover:border-blue-400 transition-colors" data-testid={`article-card-${a.slug}`}>
                <div className="aspect-[16/10] bg-slate-50 border-b border-slate-100 overflow-hidden">
                  {a.featured_image ? <img src={a.featured_image} alt="" className="w-full h-full object-cover"/> :
                    <div className="w-full h-full flex items-center justify-center"><FlaskConical className="w-8 h-8 text-blue-600/70" strokeWidth={1.25}/></div>}
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    {a.category_id && <Badge className="rounded-md font-mono text-[10px] uppercase tracking-widest bg-blue-50 text-blue-700 border border-blue-200">{a.category_id}</Badge>}
                    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400 inline-flex items-center gap-1"><Clock className="w-3 h-3"/> {a.reading_time || 1} min</span>
                  </div>
                  <div className="font-display font-semibold text-slate-900 tracking-tight text-lg">{a.title}</div>
                  {a.excerpt && <p className="mt-2 text-sm text-slate-600 line-clamp-3">{a.excerpt}</p>}
                  <div className="mt-3 flex items-center text-blue-700 text-xs font-medium">Read article <ArrowRight className="w-3.5 h-3.5 ml-1"/></div>
                </div>
              </Link>
            ))}
          </div>
      )}
    </div>
  );
}
